// Dependencies
import React, { useMemo, useState } from "react";

import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";

// Components
import { Avatar, Text as PYText } from "@performyard/py-components";

import IconButton from "../../../../../../components/Button/IconButton";
import InputCheckbox from "../../../../../../components/Inputs/Checkbox";
import Pagination from "../../../../../../components/Pagination";
import Spacer from "../../../../../../components/Spacer";
import TableLite from "../../../../../../components/TableLite";
import Text from "../../../../../../components/Text";
import Tooltip from "../../../../../../components/Tooltip";

// API
import { useEmployeeSearch, useMutateRemoveUserFromSelectionObject } from "../../../../../../api/hooks";

//utils
import get from "../../../../../../utils/GetNested";

//css
import css from "../../../Directory/Directory.module.scss";
import { sortRows } from "../../../Directory/DirectoryUtilities";

interface BaseProps {
	selectedEmployees: string[];
	setSelectedEmployees: React.Dispatch<React.SetStateAction<string[]>>;
	employeeSearchProps: {
		searchTerm: string;
		userSelectionID?: string;
		limitSelected?: boolean;
		exclusionListId?: string;
	};
	isLoading?: boolean;
	emptyText?: string;
	canBulkSelect?: boolean;
	canSingleDelete?: boolean;
}

const Directory = (props: BaseProps) => {
	const {
		selectedEmployees,
		setSelectedEmployees,
		isLoading = false,
		emptyText = "No Employees to display",
		canBulkSelect,
		canSingleDelete,
	} = props;
	const { searchTerm, userSelectionID, limitSelected = false, exclusionListId } = props.employeeSearchProps;

	const [currentPage, setCurrentPage] = useState(1);
	const [activeSortColumnId, setActiveSortColumnId] = useState<string | null>(null);
	const [activeSortDirection, setActiveSortDirection] = useState<"asc" | "desc" | null>(null);

	const { data, isFetching: isEmployeeSearchLoading } = useEmployeeSearch({
		selectionObjectID: userSelectionID,
		searchTerm: searchTerm,
		page: currentPage,
		limitSelected: limitSelected,
		usersToExcludeSelectionId: exclusionListId,
		filters: { activated: ["true"] },
		additionalKeys: [userSelectionID || ""],
	});

	const { mutate: removeUserFromSelectionObject, isPending: isRemovingUsersFromSelectionObject } =
		useMutateRemoveUserFromSelectionObject(userSelectionID || "");

	const rows = data?.users ? sortRows(data?.users, activeSortColumnId, activeSortDirection) : [];

	const handleChangePage = (page: number) => {
		setCurrentPage(page);
	};

	const handleAddUser = (userID: string) => {
		setSelectedEmployees([...(selectedEmployees || []), userID]);
	};

	const handleRemoveUser = async (userID: string) => {
		const newSelectedEmployees = selectedEmployees.filter((id) => id !== userID);
		setSelectedEmployees(newSelectedEmployees);
	};

	// handle columns
	const tableColumns = useMemo(() => {
		return [
			{
				id: "name",
				label: "Name",
				isSortable: true,
				getter: (row) => {
					const name = get(row, ["name"], "");
					const id = get(row, ["_id"], "");
					let isSelected = false;
					if (selectedEmployees && canBulkSelect) {
						isSelected = selectedEmployees.includes(row._id);
					}

					const handleToggleUser = async (userID: string) => {
						if (isSelected) {
							handleRemoveUser(userID);
						} else {
							handleAddUser(userID);
						}
					};

					return (
						<div className={`${css.nameColumn} ${canBulkSelect && css.addEmployees}`}>
							{canBulkSelect && (
								<InputCheckbox value={isSelected} onChange={() => handleToggleUser(row._id)} />
							)}

							<Avatar size={24} id={id} name={name || "User"} dangerouslySetClassName={css.avatar} />
							{name.length >= 30 ? (
								<Tooltip message={name}>
									<Text
										size="paragraph-3"
										weight="regular"
										className={`${css.employeeName} ${css.shortened}`}
									>
										{name.slice(0, 27)}...
									</Text>
								</Tooltip>
							) : (
								<Text size="paragraph-3" weight="regular" className={css.employeeName}>
									{name}
								</Text>
							)}
						</div>
					);
				},
			},
			{
				id: "Group",
				label: "PY Groups",
				getter: (row) => {
					return (
						<div>
							<div />
							<PYText
								wrap={false}
								size="paragraph-3"
								weight="regular"
								dangerouslySetClassName="py-text-truncate max-w-[250px]"
							>
								{get(row, ["data_fields", "Group", "value", "name"], "")}
							</PYText>
						</div>
					);
				},
			},
			{
				id: "Manager",
				label: "Manager",
				getter: (row) => {
					return (
						<div>
							<div />
							<PYText
								wrap={false}
								size="paragraph-3"
								weight="regular"
								dangerouslySetClassName="py-text-truncate max-w-[250px]"
							>
								{get(row, ["data_fields", "Manager", "value", "name"], "")}
							</PYText>
						</div>
					);
				},
			},
			{
				id: "singleDelete",
				label: "",
				width: canSingleDelete && !canBulkSelect ? "50px" : "0px",
				isSortable: false,
				getter: (row) => {
					return canSingleDelete && !canBulkSelect ? (
						<IconButton
							icon={faTrashCan}
							className={css.deleteButton}
							aria-label="Remove excluded employee"
							onClick={async () => {
								removeUserFromSelectionObject({ userIDs: [row._id] });
							}}
							animated
						/>
					) : (
						<div />
					);
				},
			},
		];
	}, [selectedEmployees]);

	const onColumnClick = (columnId: string, nextDirection = "desc") => {
		setActiveSortColumnId(columnId);
		setActiveSortDirection(nextDirection as "asc" | "desc");
	};

	return (
		<>
			<TableLite
				theme="vJan23"
				rowKey="_id"
				columns={tableColumns}
				rows={rows}
				onColumnClick={onColumnClick}
				activeSort={activeSortColumnId}
				activeSortDirection={activeSortDirection}
				emptyText={emptyText}
				isLoading={isLoading || isEmployeeSearchLoading || isRemovingUsersFromSelectionObject}
			/>
			<Spacer height={1} />
			<Pagination currentPage={currentPage} pageCount={data?.pages ?? 1} onClick={handleChangePage} />
		</>
	);
};

export default Directory;
